import React, { useState, useEffect, useMemo } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress, 
  TextField, 
  Box, 
  Typography,
  OutlinedInput,
  Chip,
  Tooltip
} from '@mui/material';
import { organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { EmployeeAvatar } from '../EmployeeAvatar';
import { BUSINESS_UNIT_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import { formatDateOnly } from '../../../utils/dateOnly';

// Legacy export for backward compatibility
export const RETURN_VALUE_TYPES = BUSINESS_UNIT_RETURN_TYPES;

// Flatten a hierarchy response (single root or array of roots, nested childBus) into
// pre-order rows with a `level`, as the menu renders them.
const flattenBusinessUnits = (data) => {
  const flattened = [];
  const visit = (bu, level = 0) => {
    if (bu) {
      flattened.push({ ...bu, level });
      if (bu.childBus) {
        bu.childBus.forEach(child => visit(child, level + 1));
      }
    }
  };
  if (Array.isArray(data)) {
    data.forEach(rootBu => visit(rootBu, 0));
  } else if (data) {
    visit(data, 0);
  }
  return flattened;
};

// ADR-120: lifecycle marker. `status` and `endDate` come from the API (derived
// server-side); the component never compares dates itself.
const LIFECYCLE_STYLES = {
  Ending: { color: '#B45309', backgroundColor: '#FEF3C7' },
  Inactive: { color: '#6B7280', backgroundColor: '#E5E7EB' }
};

const lifecycleLabel = (unit) => {
  if (!unit || !unit.status || unit.status === 'Active') return null;
  return unit.status === 'Ending' ? `Ends ${formatDateOnly(unit.endDate)}` : `Ended ${formatDateOnly(unit.endDate)}`;
};

const LifecycleChip = ({ unit, compact = false }) => {
  const text = lifecycleLabel(unit);
  if (!text) return null;
  return (
    <Typography
      component="span"
      data-testid="bu-lifecycle-chip"
      sx={{
        fontSize: compact ? '10px' : '11px',
        padding: compact ? '1px 4px' : '2px 6px',
        borderRadius: '4px',
        marginLeft: '4px',
        whiteSpace: 'nowrap',
        ...LIFECYCLE_STYLES[unit.status]
      }}
    >
      {text}
    </Typography>
  );
};

const BusinessUnitSelector = ({ 
  multiple = false,
  value = multiple ? [] : '', 
  onChange, 
  label = "Business Unit",
  required = false,
  disabled = false,
  fullWidth = true,
  returnValue = BUSINESS_UNIT_RETURN_TYPES.ID, // BUSINESS_UNIT_RETURN_TYPES.ID or BUSINESS_UNIT_RETURN_TYPES.NAME
  clusterId = null, // Optional: filter by cluster or fetch hierarchy for specific cluster
  size = 'medium',
  excludeRootLevel = false, // Optional: exclude root level (cluster) from selection
  disabledItemsCallback = null, // Optional: function(bu, allBus) => { disabled: boolean, reason?: string }
  showEmptyOption = true, // Optional: show "None" or custom empty option for single select
  emptyOptionLabel = "None", // Optional: custom label for empty option
  placeholder = null, // Optional: custom placeholder when no value selected
  // ADR-108 opt-in props (backward compatible — omit them for the classic behaviour):
  businessUnitsData = undefined, // Optional: pre-built hierarchy roots (childBus); skips the self-fetch and the cluster auto-clear
  loading = false, // Optional: loading state of businessUnitsData
  getItemCount = null, // Optional: function(unit) => number|null, rendered right-aligned on each option
  itemCountTooltip = null, // Optional: tooltip for the count
  hideClusterChip = false, // Optional: hide the cluster chip next to each option
  // ADR-119: render the cluster's Organizational Structure Type as a muted code
  // suffix (tooltip = full name), the same presentation ClusterSelector uses.
  // Default off; only meaningful when no cluster scope is set.
  showStructureType = false,
  emptyStateLabel = null, // Optional: disabled item shown when there are no options
  // ADR-120: off (default) = the list holds operating units only (Active + Ending);
  // on = ended units are listed too, marked "Ended <date>" (reports).
  includeInactive = false,
  ...props 
}) => {
  const isProvided = businessUnitsData !== undefined;
  const [fetchedBusinessUnits, setFetchedBusinessUnits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFetching, setIsFetching] = useState(!isProvided);
  // ADR-120: a currently selected unit that is no longer in the list because it
  // ended keeps rendering (label + chip) instead of blanking the field.
  const [pinnedUnits, setPinnedUnits] = useState({});
  const { showAlert } = useAlert();

  const businessUnits = useMemo(() => {
    const base = isProvided ? flattenBusinessUnits(businessUnitsData) : fetchedBusinessUnits;
    const visible = includeInactive ? base : base.filter(unit => !unit || unit.status !== 'Inactive');
    const pinned = Object.values(pinnedUnits).filter(unit => !visible.some(bu => bu.id === unit.id));
    return pinned.length > 0 ? [...visible, ...pinned] : visible;
  }, [isProvided, businessUnitsData, fetchedBusinessUnits, includeInactive, pinnedUnits]);
  const isLoading = isProvided ? loading : isFetching;
  // A root unit carries its cluster's name (createCluster/updateCluster keep the
  // two in step), so the chip would repeat the row's own name — show it on
  // nested units only.
  const showClusterChip = (unit) => !clusterId && !hideClusterChip && unit.level !== 0 && Boolean(unit.cluster);
  // ADR-119: with a cluster scope set the type is redundant, so it follows the
  // same rule as the cluster chip. Renders nothing when the cluster carries no
  // type (the FK is optional) or when the payload does not include it.
  const renderStructureType = (unit, fontSize = '11px') => {
    const type = showStructureType && !clusterId ? unit?.cluster?.orgStructureType : null;
    if (!type?.code) return null;
    return (
      <Typography
        component="span"
        title={type.name}
        sx={{ fontSize, color: '#6B7280', fontFamily: 'monospace', marginLeft: '4px' }}
      >
        {type.code}
      </Typography>
    );
  };

  // Filter business units based on search term, preserving ancestor hierarchy
  const filteredBusinessUnits = useMemo(() => {
    if (!searchTerm) {
      return businessUnits.filter(unit => {
        if (!unit) { return false; }
        return !(excludeRootLevel && unit.level === 0);
      });
    }

    const lowerSearch = searchTerm.toLowerCase();
    const matchingIndices = new Set();
    const ancestorIndices = new Set();

    // Find all matching items
    businessUnits.forEach((unit, index) => {
      if (unit && unit.name.toLowerCase().includes(lowerSearch)) {
        matchingIndices.add(index);
      }
    });

    // For each match, walk backward to find ancestors
    for (const matchIndex of matchingIndices) {
      let currentLevel = businessUnits[matchIndex].level;
      for (let i = matchIndex - 1; i >= 0 && currentLevel > 0; i--) {
        if (businessUnits[i].level < currentLevel) {
          ancestorIndices.add(i);
          currentLevel = businessUnits[i].level;
        }
      }
    }

    return businessUnits.filter((unit, index) => {
      if (!unit) { return false; }
      const isExcluded = excludeRootLevel && unit.level === 0;
      return !isExcluded && (matchingIndices.has(index) || ancestorIndices.has(index));
    });
  }, [businessUnits, searchTerm, excludeRootLevel]);

  useEffect(() => {
    if (isProvided) {
      return;
    }
    const fetchBusinessUnits = async () => {
      try {
        setIsFetching(true);
        let hierarchyData;
        
        // If clusterId is provided, fetch hierarchy for that specific cluster
        if (clusterId) {
          const clusterHierarchy = await organizationApi.getBusinessUnitHierarchy(clusterId, { includeInactive });
          hierarchyData = clusterHierarchy;
        } else {
          // Otherwise fetch all business units
          hierarchyData = await organizationApi.getAllBusinessUnitsHierarchy({ includeInactive });
        }
        
        setFetchedBusinessUnits(flattenBusinessUnits(hierarchyData));
      } catch (error) {
        showAlert('Failed to load business units', 'error', error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchBusinessUnits();
  }, [showAlert, clusterId, isProvided, includeInactive]);

  // ADR-120: a selected id missing from the list is looked up once. If the unit
  // ended (and belongs to the current cluster, when one is set) it is pinned so the
  // selection keeps rendering; otherwise it is recorded as checked and the
  // auto-clear effect below treats it exactly as before.
  const [checkedIds, setCheckedIds] = useState({});
  useEffect(() => { setPinnedUnits({}); setCheckedIds({}); }, [clusterId, includeInactive]);
  useEffect(() => {
    if (isLoading || returnValue === BUSINESS_UNIT_RETURN_TYPES.NAME) return undefined;
    const ids = (multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []))
      .filter(id => id && !businessUnits.some(bu => bu.id === id) && !checkedIds[id]);
    if (ids.length === 0) return undefined;
    let cancelled = false;
    Promise.all(ids.map(id => organizationApi.getBusinessUnit(id).catch(() => null)))
      .then(units => {
        if (cancelled) return;
        const ended = units.filter(unit => unit && unit.status && unit.status !== 'Active'
          && (!clusterId || unit.clusterId === clusterId || unit.cluster?.id === clusterId));
        if (ended.length > 0) {
          setPinnedUnits(prev => ({ ...prev, ...Object.fromEntries(ended.map(unit => [unit.id, { ...unit, level: 1, pinned: true }])) }));
        }
        setCheckedIds(prev => ({ ...prev, ...Object.fromEntries(ids.map(id => [id, true])) }));
      });
    return () => { cancelled = true; };
  }, [isLoading, value, multiple, businessUnits, clusterId, returnValue, checkedIds]);

  // Clear selected value if it's no longer valid after cluster change
  // (self-fetch mode only — callers providing businessUnitsData own the selection rules)
  useEffect(() => {
    if (!isProvided && clusterId && businessUnits.length > 0 && onChange) {
      // ADR-120: an id that has not been looked up yet may still turn out to be an
      // ended unit that gets pinned — do not clear it before the lookup resolves.
      const isSettled = (id) => businessUnits.some(bu => bu.id === id)
        || returnValue === BUSINESS_UNIT_RETURN_TYPES.NAME || Boolean(checkedIds[id]);
      if (multiple && Array.isArray(value)) {
        if (!value.every(isSettled)) return;
        const validBusinessUnits = value.filter(id => 
          businessUnits.some(bu => bu.id === id)
        );
        
        if (validBusinessUnits.length !== value.length) {
          onChange(validBusinessUnits);
        }
      } else if (value && isSettled(value) && !businessUnits.some(bu => bu.id === value)) {
        onChange('');
      }
    }
  }, [isProvided, clusterId, businessUnits, multiple, onChange, value, returnValue, checkedIds]);

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    
    if (onChange) {
      if (multiple) {
        if (returnValue === BUSINESS_UNIT_RETURN_TYPES.NAME) {
          const selectedNames = selectedValue.map(id => {
            const businessUnit = businessUnits.find(unit => unit.id === id);
            return businessUnit?.name || '';
          }).filter(Boolean);
          onChange(selectedNames);
        } else {
          onChange(selectedValue);
        }
      } else {
        if (returnValue === BUSINESS_UNIT_RETURN_TYPES.NAME) {
          const businessUnit = businessUnits.find(unit => unit.id === selectedValue);
          onChange(businessUnit?.name || '');
        } else {
          onChange(selectedValue || '');
        }
      }
    }
  };

  const renderItemCount = (unit) => {
    if (!getItemCount) {
      return null;
    }
    const count = getItemCount(unit);
    if (count === null || count === undefined) {
      return null;
    }
    const countElement = (
      <Typography
        component="span"
        sx={{
          ml: 'auto',
          pl: 2,
          fontSize: '0.8rem',
          fontWeight: 600,
          color: count > 0 ? '#475569' : '#94A3B8',
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {count}
      </Typography>
    );
    if (!itemCountTooltip) {
      return countElement;
    }
    return (
      <Tooltip title={itemCountTooltip} placement="right" arrow>
        {countElement}
      </Tooltip>
    );
  };

  const handleSearchChange = (event) => {
    event.stopPropagation();
    setSearchTerm(event.target.value);
  };

  // Find the selected business unit(s) for value display
  const selectedValue = (() => {
    if (!value) return multiple ? [] : '';
    
    if (multiple) {
      if (returnValue === BUSINESS_UNIT_RETURN_TYPES.NAME) {
        return value.map(name => {
          const businessUnit = businessUnits.find(unit => unit.name === name);
          return businessUnit?.id;
        }).filter(Boolean);
      } else {
        return Array.isArray(value) ? value : [];
      }
    } else {
      if (returnValue === BUSINESS_UNIT_RETURN_TYPES.NAME) {
        const businessUnit = businessUnits.find(unit => unit.name === value);
        return businessUnit?.id || '';
      } else {
        return value;
      }
    }
  })();

  const renderValue = (selected) => {
    if (multiple) {
      if (!selected || selected.length === 0) {
        return <em>{placeholder || emptyOptionLabel}</em>;
      }
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map((value) => {
            const unit = businessUnits.find(bu => bu.id === value);
            return (
              <Chip 
                key={value} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>{unit?.name}</span>
                    <LifecycleChip unit={unit} compact />
                    {unit && showClusterChip(unit) && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '10px',
                          color: '#6B7280',
                          backgroundColor: '#F3F4F6',
                          padding: '1px 4px',
                          borderRadius: '4px',
                          marginLeft: '4px'
                        }}
                      >
                        {unit.cluster.name}
                      </Typography>
                    )}
                    {renderStructureType(unit, '10px')}
                  </Box>
                }
                size="small"
              />
            );
          })}
        </Box>
      );
    }
    
    // For single select
    if (!multiple) {
      if (!selected || selected === '') {
        return placeholder ? <em>{placeholder}</em> : undefined;
      }
      // Find the selected business unit and display its name
      const unit = businessUnits.find(bu => bu.id === selected);
      if (unit) {
        // Show the full information just like in the dropdown
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {unit.level > 0 && '└ '}
            <span>{unit.name}</span>
            <LifecycleChip unit={unit} />
            {showClusterChip(unit) && (
              <Typography
                component="span"
                sx={{
                  fontSize: '11px',
                  color: '#6B7280',
                  backgroundColor: '#F3F4F6',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  marginLeft: '4px'
                }}
              >
                {unit.cluster.name}
              </Typography>
            )}
            {renderStructureType(unit)}
            {unit.owner && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                ml: 1,
                color: '#6B7280',
                fontSize: '0.85em'
              }}>
                <EmployeeAvatar employee={unit.owner} size={16} />
                <span>{unit.owner.fullName}</span>
              </Box>
            )}
          </Box>
        );
      }
    }
    
    return undefined; // Fallback
  };


  return (
    <FormControl fullWidth={fullWidth} disabled={disabled || isLoading} size={size} {...props}>
      {/* With a placeholder the field is never visually empty (displayEmpty), so the
          label must float permanently or it overlaps the placeholder text. */}
      <InputLabel shrink={placeholder ? true : undefined}>
        {label}
      </InputLabel>
      <Select
        multiple={multiple}
        value={selectedValue}
        onChange={handleChange}
        label={label}
        required={required}
        disabled={disabled || isLoading}
        displayEmpty={!!placeholder}
        notched={placeholder ? true : undefined}
        renderValue={renderValue}
        onClose={() => setSearchTerm('')}
        MenuProps={{
          autoFocus: false,
          disableAutoFocusItem: true,
          PaperProps: {
            style: {
              maxHeight: 450
            }
          }
        }}
        endAdornment={isLoading ? <CircularProgress size={20} /> : null}
      >
        {/* Search field */}
        <MenuItem sx={{ p: 0 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search business unit..."
            value={searchTerm}
            onChange={handleSearchChange}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' },
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 16px',
              }
            }}
          />
        </MenuItem>
        
        {/* Separator */}
        <MenuItem sx={{ 
          borderBottom: '1px solid #eee',
          py: 0,
          pointerEvents: 'none'
        }}/>

        {/* Empty option for non-required single select fields */}
        {!required && !multiple && showEmptyOption && (
          <MenuItem value="">
            <em>{emptyOptionLabel}</em>
          </MenuItem>
        )}

        {/* Business unit options */}
        {filteredBusinessUnits.map(unit => {
          // Check if item should be disabled using callback
          let isDisabled = false;
          let disabledReason = null;
          
          if (unit.pinned) {
            // ADR-120: kept only so the current selection renders; not a new target
            isDisabled = true;
            disabledReason = 'Ended';
          } else if (disabledItemsCallback) {
            const callbackResult = disabledItemsCallback(unit, businessUnits);
            if (callbackResult) {
              isDisabled = callbackResult.disabled || false;
              disabledReason = callbackResult.reason || null;
            }
          } else {
            // Only disable root with children if no custom callback is provided.
            // No reason text: the row is visibly dim and its children sit right
            // below it, so the greying explains itself.
            isDisabled = unit.level === 0 && unit.childBus?.length > 0;
          }
          
          return (
            <MenuItem 
              key={unit.id} 
              value={unit.id}
              disabled={isDisabled}
              sx={{
                pl: unit.level ? `${unit.level * 20 + 16}px` : '16px',
                borderLeft: unit.level ? `${unit.level}px solid #f3f4f6` : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pointerEvents: isDisabled ? 'none' : 'auto',
                color: isDisabled ? '#94A3B8' : 'inherit',
                fontWeight: unit.level === 0 ? 500 : 400,
                '&.Mui-disabled': {
                  opacity: 0.7,
                  backgroundColor: '#F1F5F9',
                  '&:hover': {
                    backgroundColor: '#F1F5F9'
                  }
                }
              }}
            >
              {unit.level > 0 && '└ '}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                <span>{unit.name}</span>
                <LifecycleChip unit={unit} />
                {showClusterChip(unit) && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '11px',
                      color: '#6B7280',
                      backgroundColor: '#F3F4F6',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      marginLeft: '4px'
                    }}
                  >
                    {unit.cluster.name}
                  </Typography>
                )}
                {renderStructureType(unit)}
                {unit.owner && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    ml: 1,
                    color: '#6B7280',
                    fontSize: '0.85em'
                  }}>
                    <EmployeeAvatar employee={unit.owner} size={16} />
                    <span>{unit.owner.fullName}</span>
                  </Box>
                )}
                {disabledReason && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 1,
                      fontSize: '0.75rem',
                      color: '#94A3B8',
                      fontStyle: 'italic'
                    }}
                  >
                    ({disabledReason})
                  </Typography>
                )}
                {renderItemCount(unit)}
              </Box>
            </MenuItem>
          );
        })}

        {emptyStateLabel && !isLoading && filteredBusinessUnits.length === 0 && (
          <MenuItem disabled sx={{ fontStyle: 'italic', color: '#94A3B8' }}>
            {emptyStateLabel}
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default BusinessUnitSelector; 